import type { CoinMode } from '@ui/hooks/useCoinMode';
import { getApy, getApyFromApr7d } from '@ui/utils/aprToApy';
import { ACTIVE_APY_MODE_LATOM, ApyMode } from '@ui/utils/constants';
import type { GraphQLClient } from 'graphql-request';

import { QueryKey } from './queryKey';

interface AprResponse {
  liquidCro: {
    apr7d: number | null;
    apr24h: number | null;
  } | null;
}

interface AprResponseLatom {
  liquidAtom: {
    apr7d: number | null;
    apr24h: number | null;
  } | null;
}

const APR_QUERY = `
query apr {
  liquidCro(id:"1") {
    apr7d
  }
}
`;

const APR_QUERY_LATOM = `
query apr {
  liquidAtom(id:"1") {
    apr7d
    apr24h
  }
}
`;

type HistoricAprsResponse = {
  liquidCroDailySnapshots: {
    apr24h: number | null;
    apr7d: number | null;
    date: string;
  }[];
};

type HistoricAprsResponseAtom = {
  liquidAtomDailySnapshots: {
    apr24h: number | null;
    apr7d: number | null;
    date: string;
  }[];
};

const HISTORIC_APRS_QUERY = `
query aprs($date: Int!) {
  liquidCroDailySnapshots(where:{date_gte: $date}) {
    date
    apr24h
    apr7d
  }
}
`;
const HISTORIC_APRS_QUERY_ATOM = `
query aprs($date: Int!) {
  liquidAtomDailySnapshots(where:{date_gte: $date}) {
    date
    apr24h
    apr7d
  }
}
`;

/**
 *
 * @param graphQlClient
 * @returns aprs info for the recent 90 days
 */
export const getHistoricApysQuery = (graphQlClient: GraphQLClient, mode?: CoinMode) => ({
  queryKey: [QueryKey.HISTORIC_APYS, mode ?? "cro"],
  queryFn: async () => {
    // get data for 90 days
    let snapshots: HistoricAprsResponse["liquidCroDailySnapshots"] = []
    if (mode === "cro") {
      const result = await graphQlClient.request<HistoricAprsResponse>(
        HISTORIC_APRS_QUERY,
        { date: Math.floor(new Date().getTime() / 1000) - 90 * 24 * 60 * 60 },
      );
      snapshots = result.liquidCroDailySnapshots
    } else {
      const result = await graphQlClient.request<HistoricAprsResponseAtom>(
        HISTORIC_APRS_QUERY_ATOM,
        { date: Math.floor(new Date().getTime() / 1000) - 90 * 24 * 60 * 60 },
      );
      snapshots = result.liquidAtomDailySnapshots
    }
    return snapshots.map((snapshot) => ({
      date: new Date(parseInt(snapshot.date) * 1000),
      apy:
        mode === "atom" && ACTIVE_APY_MODE_LATOM === ApyMode.day
          ? getApy(snapshot.apr24h ?? 0)
          : getApyFromApr7d(snapshot.apr7d ?? 0),
    }));
  },
});

export const getAprQuery = (graphQlClient: GraphQLClient) => ({
  queryKey: [QueryKey.APR, "cro"],
  queryFn: async () => {
    const result = await graphQlClient.request<AprResponse>(APR_QUERY);
    return (
      result.liquidCro?.apr7d ?? 0
    );
  },
});

export const getLatomAprQuery = (graphQlClient: GraphQLClient) => ({
  queryKey: [QueryKey.APR, "atom"],
  queryFn: async () => {
    const result = await graphQlClient.request<AprResponseLatom>(APR_QUERY_LATOM);
    return (
      (ACTIVE_APY_MODE_LATOM === ApyMode.day
        ? result.liquidAtom?.apr24h
        : result.liquidAtom?.apr7d) ?? 0
    );
  },
});